import { ChevronRight } from 'lucide-react';
import { Link } from '@tanstack/react-router';

export interface Crumb {
  label: string;
  to?: string;
  search?: Record<string, unknown>;
  /**
   * T05 (ENT-466-RECENT) — additive, optional back-navigation variant: renders a real
   * `<button>` (never a styled span/div with a handler) with the crumb's own label as
   * its accessible name. Backward-compatible: existing callers never set this, so their
   * crumbs are unaffected — `to` still wins if BOTH are somehow set, since a route link
   * is the more specific/established behavior.
   */
  onClick?: () => void;
}

export function Breadcrumbs({ crumbs }: { crumbs: Crumb[] }) {
  return (
    // AR-704 — labelled, so assistive tech and tests can both name it. The page renders THREE navs
    // (sidebar, side-tabs, this), and an unlabelled one is indistinguishable from the others: an e2e
    // test written as `locator('nav').first()` silently asserted against the SIDEBAR and passed for
    // the wrong reason. `aria-label="Breadcrumb"` is the standard landmark name for this pattern.
    <nav
      aria-label="Breadcrumb"
      className="flex items-center gap-1 text-[11px] text-text-subtle mb-1.5"
    >
      {crumbs.map((crumb, i) => (
        <span key={i} className="flex items-center gap-1">
          {i > 0 && <ChevronRight size={8} strokeWidth={2} className="text-text-subtle/40" />}
          {crumb.to ? (
            <Link to={crumb.to} search={crumb.search as never} className="hover:text-primary transition-colors">
              {crumb.label}
            </Link>
          ) : crumb.onClick ? (
            <button
              type="button"
              onClick={crumb.onClick}
              className="hover:text-primary transition-colors font-medium text-text-muted"
            >
              {crumb.label}
            </button>
          ) : (
            <span className="font-medium text-text-muted">{crumb.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}
